import React, { useState, useEffect } from "react";
import { MapPin, Navigation, RefreshCw, X, CheckCircle2, AlertCircle } from "lucide-react";
import { getRemoteWorkLocation, postRemoteWorkLocation } from "../ApiHelper/url_Helpers";
import { toasterError, toasterSuccess } from "./toaster";

const RemoteWorkLocationModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentLocationData, setCurrentLocationData] = useState(null);
  const [coords, setCoords] = useState({
    latitude: "",
    longitude: "",
    accuracy: "",
  });
  const [locationName, setLocationName] = useState("");
  const [locationError, setLocationError] = useState("");
  const [fetchingGps, setFetchingGps] = useState(false);

  // Fetch current remote work location (GET API)
  const fetchCurrentLocation = async () => {
    try {
      setLoading(true);
      const res = await getRemoteWorkLocation();
      const data = res?.data || res;
      if (data) {
        setCurrentLocationData(data);
        if (data.latitude && data.longitude) {
          setCoords({
            latitude: data.latitude.toString(),
            longitude: data.longitude.toString(),
            accuracy: data.accuracy ? data.accuracy.toString() : "10",
          });
          if (data.location_name || data.name) {
            setLocationName(data.location_name || data.name);
          } else {
            fetchReverseGeocode(data.latitude, data.longitude);
          }
        }
      }
    } catch (err) {
      console.warn("Could not fetch existing remote work location:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReverseGeocode = async (lat, lon) => {
    try {
      const bdcUrl = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`;
      const res = await fetch(bdcUrl);
      if (res.ok) {
        const data = await res.json();
        const parts = [
          data.locality || data.city,
          data.principalSubdivision,
          data.countryName,
        ].filter(Boolean);
        if (parts.length > 0) {
          setLocationName(parts.join(", "));
          return;
        }
      }
    } catch (e) {
      // fallback
    }

    try {
      const osmUrl = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`;
      const res = await fetch(osmUrl, {
        headers: { "Accept-Language": "en" },
      });
      if (res.ok) {
        const data = await res.json();
        const addr = data.address || {};
        const parts = [
          addr.suburb || addr.neighbourhood || addr.residential,
          addr.city || addr.town || addr.village,
          addr.state,
          addr.country,
        ].filter(Boolean);
        if (parts.length > 0) {
          setLocationName(parts.join(", "));
        }
      }
    } catch (e) {
      console.warn("Geocoding failed", e);
    }
  };

  const getBrowserLocation = () => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser.");
      return;
    }

    setFetchingGps(true);
    setLocationError("");

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setCoords({
          latitude: latitude.toString(),
          longitude: longitude.toString(),
          accuracy: Math.round(accuracy).toString(),
        });
        setFetchingGps(false);
        fetchReverseGeocode(latitude, longitude);
      },
      (err) => {
        setFetchingGps(false);
        let msg = "Could not fetch GPS location.";
        if (err.code === 1) {
          msg = "Location permission denied. Please allow location access in your browser.";
        } else if (err.code === 2) {
          msg = "Location unavailable. Please check your GPS/network.";
        } else if (err.code === 3) {
          msg = "Location request timed out. Please retry.";
        }
        setLocationError(msg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    if (isOpen) {
      fetchCurrentLocation();
      getBrowserLocation();
    } else {
      setLocationError("");
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!coords.latitude || !coords.longitude) {
      toasterError("Please provide valid coordinates or detect GPS location.");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        latitude: parseFloat(coords.latitude),
        longitude: parseFloat(coords.longitude),
        accuracy: parseFloat(coords.accuracy) || 10,
      };

      const res = await postRemoteWorkLocation(payload);
      toasterSuccess(res?.message || "Remote work location updated successfully!");
      if (onSuccess) onSuccess(res);
      onClose();
    } catch (err) {
      toasterError(err?.message || "Failed to update remote work location");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-cyan-600 to-sky-700 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            <h3 className="font-bold text-lg">Set Remote Work Location</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-500 gap-2">
              <RefreshCw className="w-5 h-5 animate-spin text-cyan-600" />
              <span>Fetching remote work location details...</span>
            </div>
          ) : (
            <>
              {currentLocationData && (
                <div className="bg-sky-50 border border-sky-200 rounded-xl p-3.5 text-xs text-sky-800">
                  <div className="font-semibold text-sky-900 mb-1 flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-sky-600" /> Current Configured Location:
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <div>
                      <span className="font-medium">Latitude:</span>{" "}
                      {currentLocationData.latitude ?? "N/A"}
                    </div>
                    <div>
                      <span className="font-medium">Longitude:</span>{" "}
                      {currentLocationData.longitude ?? "N/A"}
                    </div>
                    {currentLocationData.accuracy && (
                      <div>
                        <span className="font-medium">Accuracy:</span>{" "}
                        {currentLocationData.accuracy}m
                      </div>
                    )}
                    {(currentLocationData.location_name || currentLocationData.name) && (
                      <div className="col-span-2">
                        <span className="font-medium">Location:</span>{" "}
                        {currentLocationData.location_name || currentLocationData.name}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* GPS Fetch Button & Status */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                    <Navigation className="w-4 h-4 text-cyan-600" /> Detect Current GPS
                  </span>
                  <button
                    type="button"
                    onClick={getBrowserLocation}
                    disabled={fetchingGps}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 hover:border-cyan-500 text-xs font-semibold text-gray-700 rounded-lg shadow-sm transition-all disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${fetchingGps ? "animate-spin text-cyan-600" : ""}`} />
                    {fetchingGps ? "Detecting..." : "Detect Again"}
                  </button>
                </div>

                {locationName && (
                  <div className="text-xs text-gray-600 bg-white p-2.5 rounded-lg border border-gray-200">
                    <span className="font-semibold text-gray-700">Detected Area: </span>
                    {locationName}
                  </div>
                )}

                {locationError && (
                  <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 p-2.5 rounded-lg border border-red-200">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{locationError}</span>
                  </div>
                )}
              </div>

              {/* Coordinates Inputs */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-700 block mb-1">
                      Latitude <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="any"
                      required
                      value={coords.latitude}
                      onChange={(e) => setCoords({ ...coords, latitude: e.target.value })}
                      placeholder="e.g. 17.4483"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-gray-700 block mb-1">
                      Longitude <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="any"
                      required
                      value={coords.longitude}
                      onChange={(e) => setCoords({ ...coords, longitude: e.target.value })}
                      placeholder="e.g. 78.3915"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">
                    Accuracy (meters)
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={coords.accuracy}
                    onChange={(e) => setCoords({ ...coords, accuracy: e.target.value })}
                    placeholder="e.g. 12.5"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                <div className="pt-2 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 hover:bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || !coords.latitude || !coords.longitude}
                    className="px-5 py-2 bg-gradient-to-r from-cyan-600 to-sky-700 hover:from-cyan-700 hover:to-sky-800 text-white rounded-lg text-sm font-semibold shadow-md transition-all disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {submitting ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Location"
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RemoteWorkLocationModal;
